<script setup lang="ts">
import { getBrowserInfo } from '@/utils/BrowserInfo';
import { moment } from '@/utils/useTimeZone';
import { computed, ref, watch } from 'vue';

interface Props {
  modelValue?: string;
  label?: string;
  placeholder?: string;
  hint?: string;
  rules?: any[];
  disabled?: boolean;
  readonly?: boolean;
  clearable?: boolean;
  persistentHint?: boolean;
  required?: boolean;
  minDate?: string;
  maxDate?: string;
  error?: boolean;
  errorMessages?: string | string[];
  showYear?: boolean;
  format?: string;
  showTime?: boolean;
  allowedDates?: (date: any) => boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: 'Date',
  placeholder: 'DD-MM-YYYY',
  hint: '',
  rules: () => [],
  disabled: false,
  readonly: false,
  clearable: true,
  persistentHint: false,
  required: false,
  error: false,
  showYear: false,
  format: 'DD-MM-YYYY',
  showTime: false
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const parseModelValue = (val: string | undefined): { date: string; time: string } => {
  if (!val) return { date: '', time: '00:00' };
  const m = moment(val);
  if (m.isValid()) return { date: m.format('YYYY-MM-DD'), time: m.format('HH:mm') };
  return { date: val.split(' ')[0] || '', time: '00:00' };
};

const { date: initDate, time: initTime } = parseModelValue(props.modelValue);
const internalValue = ref(initDate);
const timeValue = ref(initTime);
const isMobile = ref(false);
const menu = ref(false);

watch(() => props.modelValue, (newVal) => {
  const { date, time } = parseModelValue(newVal);
  internalValue.value = date;
  timeValue.value = time;
});

const emitValue = () => {
  if (!internalValue.value) {
    emit('update:modelValue', '');
    return;
  }
  emit('update:modelValue', props.showTime ? `${internalValue.value} ${timeValue.value}` : internalValue.value);
};

// Handle date picker selection — keep menu open when time picker is also shown
const handleDateSelect = (date: any) => {
  if (date) {
    internalValue.value = moment(date).format('YYYY-MM-DD');
    if (!props.showTime) {
      emitValue();
      menu.value = false;
    }
  }
};

const handleTimeUpdate = (value: string | null) => {
  if (value) timeValue.value = value;
  emitValue();
};

const handleInput = (value: string) => {
  internalValue.value = value;
  emitValue();
};

const handleOk = () => {
  emitValue();
  menu.value = false;
};

const handleNativeChange = (e: Event) => {
  const val = (e.target as HTMLInputElement).value;
  if (!val) {
    internalValue.value = '';
    timeValue.value = '00:00';
  } else if (props.showTime) {
    internalValue.value = val.slice(0, 10);
    timeValue.value = val.slice(11) || '00:00';
  } else {
    internalValue.value = val;
  }
  emitValue();
};

const nativeInputValue = computed(() => {
  if (!internalValue.value) return '';
  return props.showTime ? `${internalValue.value}T${timeValue.value}` : internalValue.value;
});

const displayValue = computed(() => {
  if (!internalValue.value) return '';
  const dateStr = props.showTime ? `${internalValue.value} ${timeValue.value}` : internalValue.value;
  return moment(dateStr).format(props.format);
});

onMounted(async() => {
  const browserInfo = await getBrowserInfo();
  isMobile.value = browserInfo.os === 'Android' || browserInfo.os === 'iOS';
});
</script>

<template>
  <!-- Mobile: styled field with transparent native date input overlaid -->
  <div v-if="isMobile" style="position: relative;">
    <v-text-field
      :model-value="displayValue"
      :validation-value="internalValue"
      :label="label"
      :placeholder="placeholder"
      :hint="hint"
      :rules="rules"
      :disabled="disabled"
      :readonly="true"
      :clearable="disabled || readonly ? false : clearable"
      :persistent-hint="persistentHint"
      :error="error"
      :error-messages="errorMessages"
      @click:clear="() => { internalValue = ''; emitValue(); }"
    >
      <template #append-inner>
        <v-icon v-if="!disabled && !readonly">
          {{ showTime ? 'mdi-calendar-clock' : 'mdi-calendar' }}
        </v-icon>
      </template>
    </v-text-field>
    <input
      v-if="!disabled && !readonly"
      :type="showTime ? 'datetime-local' : 'date'"
      :value="nativeInputValue"
      :min="minDate"
      :max="maxDate"
      style="position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer;"
      @change="handleNativeChange"
    />
  </div>

  <!-- Desktop: existing styled picker -->
  <v-menu
    v-else
    v-model="menu"
    :close-on-content-click="false"
    location="top"
    min-width="auto"
    class="DateInput"
  >
    <template #activator="{ props: menuProps }">
      <v-text-field
        :model-value="displayValue"
        :validation-value="internalValue"
        :label="label"
        :placeholder="placeholder"
        :hint="hint"
        :rules="rules"
        :disabled="disabled"
        :readonly="readonly"
        :clearable="disabled || readonly ? false : clearable"
        :persistent-hint="persistentHint"
        :error="error"
        :error-messages="errorMessages"
        v-bind="!disabled && !readonly ? menuProps : {}"
        @update:model-value="handleInput"
        @click:clear="() => { internalValue = ''; emitValue(); }"
      >
        <template #append-inner>
          <v-icon v-if="!disabled && !readonly">
            {{ showTime ? 'mdi-calendar-clock' : 'mdi-calendar' }}
          </v-icon>
        </template>
      </v-text-field>
    </template>

    <v-date-picker
      :model-value="internalValue || null"
      :min="minDate"
      :max="maxDate"
      :disabled="disabled"
      :allowed-dates="allowedDates"
      hide-title
      show-adjacent-months
      hide-header
      @update:model-value="handleDateSelect"
    >
      <template v-if="!showYear" v-slot:controls="{ disabled, nextMonth, prevMonth, monthYearText }">
        <v-btn :disabled="disabled.includes('prev-month')" color="primary" icon="$prev" @click="prevMonth"></v-btn>
        <v-spacer></v-spacer>
        <div class="d-flex justify-center align-center gap-3">
          <h3>{{ monthYearText.split(' ')[0] }}</h3>
          <h3>{{ monthYearText.split(' ')[1] }}</h3>
        </div>
        <v-spacer></v-spacer>
        <v-btn :disabled="disabled.includes('next-month')" color="primary" icon="$next" @click="nextMonth"></v-btn>
      </template>
      <template #actions>
        <v-time-picker
          v-if="showTime"
          class="TimePicker"
          :model-value="timeValue"
          @update:model-value="handleTimeUpdate"
        />
        <div class="d-flex justify-end">
          <v-btn variant="text" @click="menu = false">Cancel</v-btn>
          <v-btn variant="text" color="primary" @click="handleOk">OK</v-btn>
        </div>
      </template>
    </v-date-picker>
  </v-menu>
</template>

<style>
.DateInput {
  .v-picker__actions {
    flex-direction: column;
    align-items: normal;
  }

  .v-picker__actions .TimePicker .v-btn:not(:last-child) {
    margin-right: 0 !important;
  }

  .TimePicker {
    width: 100%;
    min-width: 300px;
    .v-picker__body {
      display: none;
    }
  }

  .TimePicker {
    .v-btn__content {
      color: rgba(var(--v-theme-grey-900)) !important;
    }

    .v-btn__overlay {
      opacity: 0.35 !important;
      background: rgb(var(--v-theme-grey-400));
    }
  }

  .TimePicker .v-btn--active {
    background-color: rgb(var(--v-theme-primary));

    .v-btn__content {
      color: rgba(var(--v-theme-on-primary)) !important;
    }

    > .v-btn__overlay {
      opacity: 0 !important;
    }
  }
}
</style>
